import { useState } from "react";
import { Copy, Check, Linkedin, BookOpen } from "lucide-react";

function CopyButton({ getText }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const el = document.createElement("textarea");
      el.value = getText();
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-md transition-all duration-200 ${
        copied
          ? "bg-green-500 text-white"
          : "bg-[#F4F4F4] text-[#0F0F0F] hover:bg-[#FF0000] hover:text-white"
      }`}
    >
      {copied ? (
        <>
          <Check size={14} />
          Copied!
        </>
      ) : (
        <>
          <Copy size={14} />
          Copy
        </>
      )}
    </button>
  );
}

function LinkedInCard({ post }) {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col">
      {/* Red top border accent */}
      <div className="h-1 bg-[#FF0000]" />
      <div className="p-6 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#0A66C2] flex items-center justify-center">
              <Linkedin size={18} className="text-white" fill="white" />
            </div>
            <span className="font-bold text-[#0F0F0F] text-sm uppercase tracking-wider">
              LinkedIn Post
            </span>
          </div>
          <CopyButton getText={() => post} />
        </div>
        {/* Content */}
        <div className="flex-1 text-[#0F0F0F] text-sm leading-relaxed whitespace-pre-wrap font-medium">
          {post}
        </div>
      </div>
    </div>
  );
}

function BlogCard({ blogPost }) {
  const fullText = `${blogPost.headline}\n\n${blogPost.body}`;

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col">
      {/* Red top border accent */}
      <div className="h-1 bg-[#FF0000]" />
      <div className="p-6 flex flex-col flex-1">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#0F0F0F] flex items-center justify-center">
              <BookOpen size={18} className="text-white" />
            </div>
            <span className="font-bold text-[#0F0F0F] text-sm uppercase tracking-wider">
              Blog / Newsletter
            </span>
          </div>
          <CopyButton getText={() => fullText} />
        </div>
        {/* Headline */}
        <h2 className="text-[#0F0F0F] font-black text-xl leading-tight mb-4">
          {blogPost.headline}
        </h2>
        {/* Body */}
        <div className="flex-1 text-[#0F0F0F] text-sm leading-relaxed whitespace-pre-wrap">
          {blogPost.body}
        </div>
      </div>
    </div>
  );
}

export default function ResultCards({ linkedin_post, blog_post }) {
  return (
    <div className="w-full max-w-6xl mx-auto px-4 pb-16">
      <div className="mb-8 text-center">
        <p className="text-[#FF0000] font-bold text-sm uppercase tracking-widest mb-1">
          Done
        </p>
        <h2 className="text-white font-black text-3xl">
          Your content is ready.
        </h2>
        <p className="text-gray-400 mt-2 text-sm">
          Copy and publish — no edits needed.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <LinkedInCard post={linkedin_post} />
        <BlogCard blogPost={blog_post} />
      </div>
    </div>
  );
}
